/**
 * @fileoverview The org's instrument, serial and note on one student of one
 * class (F2 / O9, decision D4), edited in place.
 *
 * Three small fields under the student's name. Each saves on its own when
 * it loses focus or takes Enter, optimistically: the row shows the new value
 * at once, and a refused save puts the old one back with a toast. Only the
 * field that changed is sent, so two administrators editing different
 * fields do not overwrite each other.
 *
 * Nothing here is the instructor's `students.notes`, and nothing here reaches
 * a family. The help line says so, in the organization's own words.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";

// Placeholders are the field's name, never an example value: an empty row
// reading "Cello · A-1183" in grey would look like a cello nobody assigned.
export const ANNOTATION_FIELDS = [
  { key: "instrument", label: "Instrument", placeholder: "Instrument", max: 120 },
  { key: "instrument_serial", label: "Serial", placeholder: "Serial", max: 60 },
  { key: "note", label: "Note", placeholder: "Note", max: 500 },
];

const INPUT =
  "w-full rounded-lg border border-transparent bg-transparent px-2 py-1 font-sans text-[13px] text-[var(--text-primary)] " +
  "placeholder:text-[var(--text-tertiary)] hover:border-[var(--studio-divider)] focus:border-[var(--studio-divider-strong)] " +
  "focus:bg-[var(--studio-raised)] focus:outline-none";

/**
 * @param {{ classId: string, studentId: string, annotation: object|null, onSaved: Function, disabled?: boolean }} props
 */
export default function RosterAnnotationFields({ classId, studentId, annotation, onSaved, disabled = false }) {
  const [draft, setDraft] = useState(() => fromAnnotation(annotation));
  const [saving, setSaving] = useState(null);

  // A save elsewhere (or a reload) refreshes what the fields show.
  useEffect(() => {
    setDraft(fromAnnotation(annotation));
  }, [annotation]);

  const commit = async (key) => {
    const value = (draft[key] || "").trim();
    const before = (annotation?.[key] || "").trim();
    if (value === before) {
      setDraft((d) => ({ ...d, [key]: value }));
      return;
    }
    setSaving(key);
    // Optimistic: the row shows the new value while the save is in flight.
    const optimistic = { ...(annotation || {}), [key]: value || null };
    onSaved?.(studentId, hasAny(optimistic) ? optimistic : null);
    try {
      const res = await orgApi.put(`/org/classes/${classId}/roster/${studentId}/annotation`, { [key]: value });
      onSaved?.(studentId, res.data?.annotation ?? null);
    } catch (error) {
      onSaved?.(studentId, annotation || null);
      setDraft((d) => ({ ...d, [key]: before }));
      toast.error(getOrgErrorMessage(error, "Couldn't save that"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)]" data-testid={`annotation-${studentId}`}>
      {ANNOTATION_FIELDS.map((f) => (
        <label key={f.key} className="block">
          <span className="sr-only">{f.label}</span>
          <input
            className={INPUT}
            value={draft[f.key] || ""}
            placeholder={f.placeholder}
            maxLength={f.max}
            disabled={disabled || saving === f.key}
            aria-label={`${f.label} for this student`}
            data-testid={`annotation-${studentId}-${f.key}`}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
            onBlur={() => commit(f.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                setDraft((d) => ({ ...d, [f.key]: annotation?.[f.key] || "" }));
                e.currentTarget.blur();
              }
            }}
          />
        </label>
      ))}
    </div>
  );
}

function fromAnnotation(a) {
  return Object.fromEntries(ANNOTATION_FIELDS.map((f) => [f.key, a?.[f.key] || ""]));
}

function hasAny(a) {
  return ANNOTATION_FIELDS.some((f) => (a?.[f.key] || "").trim());
}

/** One line for a reader: "Cello · A-1183 · Peanut allergy". */
export function annotationLine(a) {
  return ANNOTATION_FIELDS.map((f) => a?.[f.key]).filter(Boolean).join(" · ");
}
